import { TRIAL_CLASS_BANNER_REDIRECT_LINK } from '@common/constants'
import { CClient } from '@common/utils'
import Footer from '@components/common/footer'
import NavBar from '@components/common/navbar'
import Spinner from '@components/common/spinner'
import TextImageCommonFold from '@components/common/text-image-common-fold'
import CompetitionCards from '@components/competitions/competition-cards'
import Faq from '@components/competitions/competition-faq'
import SmallBanner from '@components/competitions/free-class-banner/mobileViewBanner'
import QuizBanner from '@components/competitions/quiz-banner'
import AuthModal from '@containers/competitions/auth-modal.dynamic'
import { useSessionContext } from '@contexts/session'
import useLazyLoadCompetitionList from '@hooks/competitions/useLazyLoadCompetitionList'
import { USER_COUNTRY_FROM_IP_COOKIE } from '@lib/constants/common'
import {
  COMPETITION_LIST_SSR_PAGE_SIZE,
  COMPETITION_LIST_SECTIONS,
  COMPETITION_TYPES,
} from '@lib/constants/competitions'
import { competitionsListSeo } from '@lib/seo'
import { CompetititonsListType } from '@lib/types/competitions'
import {
  formatCountryQuery,
  getCountryFromIP,
  redirectToForGetInitialProps,
} from '@lib/utils/common'
import {
  categorizeAndSortCompetitions,
  getDefaultActiveTab,
  getDimensions,
} from '@lib/utils/competitions'
import { NextPage } from 'next'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import { parseCookies } from 'nookies'
import { useCallback, useEffect, useRef, useState } from 'react'

type Props = {
  upcomingCompetitions: CompetititonsListType
  liveCompetitions: CompetititonsListType
  completedCompetitions: CompetititonsListType
  practiceCompetitions: CompetititonsListType
  defaultVisibleSection?: string
}

const SECTION_TOP_OFFSET_FOR_SCROLL = 10

const { LIVE, UPCOMING, COMPLETED, PRACTICE } = COMPETITION_LIST_SECTIONS

const Competitions: NextPage<Props> = ({
  upcomingCompetitions,
  liveCompetitions,
  completedCompetitions,
  practiceCompetitions,
  defaultVisibleSection,
}: Props) => {
  const { loggedIn, isPaidUser } = useSessionContext()
  const [isOpen, setIsOpen] = useState(false)
  const [competitions, setCompetitions] = useState({
    upcomingCompetitions,
    liveCompetitions,
    completedCompetitions,
    practiceCompetitions,
  })
  const [selectedSlug, setSelectedSlug] = useState(null)
  const [visibleSection, setVisibleSection] = useState('')
  const router = useRouter()
  const liveRef = useRef(null)
  const upcomingRef = useRef(null)
  const completedRef = useRef(null)
  const headerRef = useRef(null)
  const practiceRef = useRef(null)

  const sectionRefs = [
    { section: LIVE, ref: liveRef, sectionTitle: 'Live' },
    { section: UPCOMING, ref: upcomingRef, sectionTitle: 'Upcoming' },
    { section: PRACTICE, ref: practiceRef, sectionTitle: 'Practice' },
    { section: COMPLETED, ref: completedRef, sectionTitle: 'Completed' },
  ]

  const scrollTo = (element: HTMLElement) => {
    if (!headerRef.current) return
    const { height: headerHeight } = getDimensions(headerRef.current)
    window.scrollBy({
      top:
        element.getBoundingClientRect().top -
        headerHeight +
        SECTION_TOP_OFFSET_FOR_SCROLL,
      behavior: 'smooth',
    })
  }

  const {
    infiniteRef,
    isLoading,
    completedSectionListCount,
  } = useLazyLoadCompetitionList({
    setCompetitions,
  })

  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current) return
      const { height: headerHeight } = getDimensions(headerRef.current)
      const scrollPosition = window.scrollY + headerHeight
      const selected = sectionRefs.find(({ ref }) => {
        const element = ref.current
        if (element) {
          const { offsetBottom, offsetTop } = getDimensions(element)
          return scrollPosition > offsetTop && scrollPosition < offsetBottom
        }
      })

      if (selected && selected.section !== visibleSection) {
        setVisibleSection(selected.section)
      } else if (!selected && visibleSection) {
        setVisibleSection(defaultVisibleSection)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [visibleSection])

  useEffect(() => {
    setVisibleSection(defaultVisibleSection)
  }, [])

  const handleRegisterNow = (slug) => {
    if (loggedIn) {
      router.push({
        pathname: `/competitions/${slug}/register`,
        query: router.query,
      })
    } else {
      setSelectedSlug(slug)
      setIsOpen(true)
    }
  }

  const getSectionListCount = useCallback(
    (section: string) => {
      if (section === LIVE) return competitions.liveCompetitions.length
      else if (section === PRACTICE)
        return competitions.practiceCompetitions.length
      else if (section === UPCOMING)
        return competitions.upcomingCompetitions.length
      else if (section === COMPLETED)
        // Completed competitions are lazy loaded
        return completedSectionListCount
      return 0
    },
    [competitions]
  )

  return (
    <>
      <NextSeo {...competitionsListSeo} />
      <div className="flex flex-col h-full min-h-screen bg-grey-100">
        <NavBar next={router.asPath} />
        <AuthModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          slug={selectedSlug}
          redirectionUrl={`/competitions/${selectedSlug}/register`}
          isGradeFieldShown
          ctaText="Register now"
        />
        <div className="container flex justify-center pt-10 mx-auto md:py-10 md:px-10">
          <div className="flex flex-wrap justify-between pb-10 md:w-4/5 md:pr-4">
            <div className="flex flex-col flex-1">
              <h1 className="mx-5 mb-2 text-2xl lg:text-3xl font-600 text-grey md:mx-0">
                All coding competitions, challenges & hackathons
              </h1>
              <div
                className="sticky top-0 z-10 mx-5 mt-4 border-b bg-grey-100 border-grey-500 md:mx-0"
                ref={headerRef}
              >
                {sectionRefs.map(({ section, ref, sectionTitle }) => {
                  return (
                    (section === 'completed' ||
                      getSectionListCount(section) > 0) && (
                      <button
                        key={section}
                        className={`text-xl mr-6 md:mr-8 py-3 focus:outline-none hover:text-orange ${
                          visibleSection === section &&
                          'border-b-2 border-orange text-orange font-600'
                        }`}
                        onClick={() => {
                          scrollTo(ref.current)
                        }}
                      >
                        {sectionTitle}{' '}
                        {getSectionListCount(section) > 0 && (
                          <span className="hidden px-2 py-1 font-sans text-sm text-white bg-orange-500 rounded font-600 md:inline">
                            {getSectionListCount(section)}
                          </span>
                        )}
                      </button>
                    )
                  )
                })}
              </div>
              <div ref={infiniteRef}>
                {getSectionListCount(LIVE) > 0 && (
                  <div id="live" ref={liveRef} className="pt-6">
                    <CompetitionCards
                      handleRegisterNow={handleRegisterNow}
                      competitionList={competitions.liveCompetitions}
                    />
                  </div>
                )}
                {getSectionListCount(UPCOMING) > 0 && (
                  <div id="upcoming" ref={upcomingRef}>
                    <div className="flex items-end pt-6 pb-2 mx-5 md:mx-0">
                      {getSectionListCount(LIVE) > 0 && (
                        <>
                          <h2 className="text-xl font-600 text-orange">
                            Upcoming
                          </h2>
                          <div className="w-full h-0 mb-3 ml-2 border-t text-grey-500" />
                        </>
                      )}
                    </div>
                    <CompetitionCards
                      handleRegisterNow={handleRegisterNow}
                      competitionList={competitions.upcomingCompetitions}
                    />
                  </div>
                )}
                <div id="practice" ref={practiceRef}>
                  <div className="flex items-end pt-6 pb-2 mx-5 md:mx-0">
                    {getSectionListCount(PRACTICE) > 0 && (
                      <>
                        <h2 className="text-xl font-600 text-orange">
                          Practice
                        </h2>
                        <div className="w-full h-0 mb-3 ml-2 border-t text-grey-500" />
                      </>
                    )}
                  </div>
                  <CompetitionCards
                    handleRegisterNow={handleRegisterNow}
                    competitionList={competitions.practiceCompetitions}
                  />
                </div>

                <div id="completed" ref={completedRef}>
                  <div className="flex items-end pt-6 pb-2 mx-5 md:mx-0">
                    {(getSectionListCount(LIVE) > 0 ||
                      getSectionListCount(UPCOMING) > 0) && (
                      <>
                        <h2 className="text-xl font-600 text-orange">
                          Completed
                        </h2>
                        <div className="w-full h-0 mb-3 ml-2 border-t text-grey-500" />
                      </>
                    )}
                  </div>
                  <CompetitionCards
                    handleRegisterNow={handleRegisterNow}
                    competitionList={competitions.completedCompetitions}
                  />
                </div>
              </div>
              {isLoading && (
                <div className="flex items-center justify-center py-20">
                  <Spinner classes="text-orange w-10 h-10" />
                </div>
              )}
            </div>
          </div>

          <div className="hidden max-w-xs pt-2 mt-40 md:block">
            <div className={`pb-5 ${isPaidUser && 'sticky top-5'} `}>
              <QuizBanner isBannerOnCompetitionListPage />
            </div>
            {!isPaidUser && (
              <div className="sticky flex flex-col top-5">
                <SmallBanner
                  redirectLink={
                    loggedIn
                      ? TRIAL_CLASS_BANNER_REDIRECT_LINK.LOGGED_IN
                      : TRIAL_CLASS_BANNER_REDIRECT_LINK.LOGGED_OUT
                  }
                />
              </div>
            )}
          </div>
        </div>
        <div className="block mx-5 md:hidden">
          <div className="py-5">
            <QuizBanner isBannerOnCompetitionListPage />
          </div>
          {/* <SmallBanner redirectLink={FREE_CLASS_BOOKING_BANNER_TRACKING_URL} /> */}
        </div>
        <TextImageCommonFold
          title={
            <>
              <span className="yellow-underline">How coding contests work</span>
            </>
          }
          description={
            <>
              <p>
                Codingal’s coding contests are designed to push students beyond
                their comfort zones, immersing them in real-world coding
                experiences. Participants work on exciting projects such as
                building apps, games, and websites, collaborating with peers in
                a dynamic, educational environment. With expert guidance,
                mentorship, and support, students can earn certifications and
                accolades as they showcase their skills through fun,
                skill-building challenges.
              </p>
              <p>
                These contests are in partnership with leading institutions like
                IIT Kanpur Antaragni, USA Computer Olympiad (USACO), HPE, the
                Indian Institute of Science, IIT Roorkee, IIT Guwahati, IIT
                Bombay, IIT Dhanbad, and BITS Pilani, providing students with
                exclusive opportunities and recognition from top organizations.
              </p>
              <p>
                Our contests cater to all skill levels—beginner, intermediate,
                and advanced—ensuring everyone has a chance to grow. These
                events go beyond just coding; they foster problem-solving,
                logical thinking, and confidence in a creative, supportive
                space. Students tackle real-world scenarios, collaborate with
                peers, and earn valuable rewards for their efforts, all while
                developing essential skills for the future.
              </p>
              <p>
                The contests also offer amazing prizes like exclusive tech
                gadgets, scholarships, and certificates from prestigious
                partners such as IITs, BITS Pilani, and USACO. These prizes not
                only celebrate students’ hard work but also enhance their
                learning portfolios, providing a competitive advantage for
                future academic and career opportunities.
              </p>
            </>
          }
          imagePath="/images/competitions/coding-camp"
          imageWidth={370}
          imageHeight={370}
        />
        <TextImageCommonFold
          title={
            <>
              Our competitions are appropriate for{' '}
              <span className="yellow-underline">
                Our competitions are appropriate for
              </span>
            </>
          }
          description={
            <>
              <p>
                Our coding competitions and hackathons are designed for kids
                aged 6-18, offering challenges for every grade, from K-12.
                Whether you’re a high schooler building your resume or an
                elementary student exploring the basics of coding, there’s
                something for everyone. Participants can earn certifications,
                win prizes, and gain recognition from top institutions. These
                competitions also provide valuable preparation for future
                competitive exams and offer students the opportunity to showcase
                their winning projects. Join Codingal’s competitions and
                hackathons to turn your passion for coding into achievements
                that highlight your growth, learning, and dedication!
              </p>
            </>
          }
          imagePath="/images/competitions/coding-for-school-kids"
          imageWidth={370}
          imageHeight={370}
        />
        <Faq />
      </div>

      <Footer />
    </>
  )
}

Competitions.getInitialProps = async (context) => {
  const token = parseCookies(context).token
  const countryCode =
    parseCookies(context)?.[USER_COUNTRY_FROM_IP_COOKIE] ||
    (await getCountryFromIP(context))
  const countryQuery = formatCountryQuery(countryCode)
  const response = await CClient(
    `/api/v1/competitions/?page_size=${COMPETITION_LIST_SSR_PAGE_SIZE}&competition_type=${COMPETITION_TYPES.CODING_CONTEST}&competition_type=${COMPETITION_TYPES.HACKATHON}&state=Upcoming&state=Ongoing${countryQuery}`,
    {
      method: 'GET',
      headers: {
        Authorization: token ? `Token ${token}` : null,
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    redirectToForGetInitialProps(context, `/${response.status}/`)
    return {
      upcomingCompetitions: [],
      liveCompetitions: [],
      completedCompetitions: [],
      practiceCompetitions: [],
    }
  }

  if (response.ok) {
    const data = await response.json()
    const categorizedCompetitions = categorizeAndSortCompetitions(data.results)
    const defaultVisibleSection = getDefaultActiveTab(categorizedCompetitions)
    return {
      upcomingCompetitions: categorizedCompetitions.upcoming,
      liveCompetitions: categorizedCompetitions.live,
      completedCompetitions: categorizedCompetitions.completed,
      practiceCompetitions: categorizedCompetitions.practice,
      defaultVisibleSection,
    }
  }
}

export default Competitions
